import { useState } from "react";
import { config, renderTokens } from "@/config/eventConfig";
import { usePersistentState } from "@/hooks/usePersistentState";

const p3 = config.ui.part3;

const getSpec = (challenge, variantId) =>
  challenge?.variants
    ? challenge.variants.find((v) => v.id === variantId) || null
    : challenge;

export const PromptChallengeChoice = () => {
  const [mode, setMode] = usePersistentState("ps.mode", "own");
  const [teamName, setTeamName] = usePersistentState("ps.teamName", "");
  const [driving, setDriving] = usePersistentState("ps.driving", null);
  const [challengeId, setChallengeId] = usePersistentState("ps.challengeId", null);
  const [variantId, setVariantId] = usePersistentState("ps.variantId", null);
  const [values, setValues] = useState({});
  const [assembled, setAssembled] = useState("");
  const [copied, setCopied] = useState(false);

  const challenge = config.challenges.find((c) => c.id === challengeId) || null;
  const spec = getSpec(challenge, variantId);

  const pickChallenge = (id) => {
    setChallengeId(id);
    setVariantId(null);
    setValues({});
    setAssembled("");
  };
  const resetChallenge = () => {
    setChallengeId(null);
    setVariantId(null);
    setValues({});
    setAssembled("");
  };

  const generate = () => {
    const ctx = spec.fields
      .map((f) => {
        const v = (values[f.key] || "").trim();
        return v ? `${f.label}: ${v}` : null;
      })
      .filter(Boolean)
      .join("\n");
    const parts = [
      renderTokens(spec.rtcf.role),
      renderTokens(spec.rtcf.task),
      ctx ? `Context:\n${ctx}` : "",
      renderTokens(spec.rtcf.format),
    ].filter(Boolean);
    setAssembled(parts.join("\n\n"));
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(assembled);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable
    }
  };

  const bigBtn =
    "px-5 py-4 border text-left transition-colors duration-150 flex items-center justify-between";

  return (
    <div data-testid="prompt-challenge-choice">
      {/* Top controls */}
      <div className="bg-white border border-sand p-5 mb-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            data-testid="part3-toggle-own"
            onClick={() => setMode("own")}
            className={`${bigBtn} ${
              mode === "own"
                ? "border-teal text-teal"
                : "border-sand text-ink/70 hover:border-orange hover:text-orange-dark"
            }`}
          >
            {p3.toggle_own}
          </button>
          <button
            data-testid="part3-toggle-team"
            onClick={() => setMode("team")}
            className={`${bigBtn} ${
              mode === "team"
                ? "border-teal text-teal"
                : "border-sand text-ink/70 hover:border-orange hover:text-orange-dark"
            }`}
          >
            {p3.toggle_team}
          </button>
        </div>

        {mode === "team" && (
          <div className="mt-4">
            <label className="mono-label text-ink/60 block mb-2">
              {p3.team_name_label}
            </label>
            <input
              data-testid="part3-team-name"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              className="w-full max-w-md px-3 py-2 bg-cream/40 border border-sand focus:border-orange focus:outline-none"
            />
          </div>
        )}
      </div>

      {/* Team: which laptop is driving */}
      {mode === "team" && !driving ? (
        <div data-testid="driving-question">
          <p className="text-lg mb-4">{p3.driving_question}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
            <button
              data-testid="part3-driving-yes"
              onClick={() => setDriving("yes")}
              className={`${bigBtn} border-sand hover:border-orange hover:text-orange-dark`}
            >
              {p3.driving_yes}
              <span className="text-orange">&rarr;</span>
            </button>
            <button
              data-testid="part3-driving-no"
              onClick={() => setDriving("no")}
              className={`${bigBtn} border-sand hover:border-orange hover:text-orange-dark`}
            >
              {p3.driving_no}
              <span className="text-orange">&rarr;</span>
            </button>
          </div>
        </div>
      ) : !challengeId ? (
        <ChallengeCards onPick={pickChallenge} />
      ) : mode === "team" && driving === "no" ? (
        <div>
          <div data-testid="standby-card" className="bg-white border border-sand p-8">
            <p className="mono-label text-ink/50 mb-2">
              {teamName || "Your team"}
            </p>
            <h3 className="font-display text-3xl leading-none mb-3">
              {challenge.title}
            </h3>
            <p className="text-ink/70 text-lg">{p3.standby_line}</p>
          </div>
          <ChangeLink onClick={resetChallenge} />
        </div>
      ) : (
        <div>
          {/* Policy variant selector */}
          {challenge.variants && !variantId ? (
            <div data-testid="variant-selector">
              <h3 className="font-display text-3xl leading-none mb-1">
                {challenge.title}
              </h3>
              <p className="text-ink/60 mb-6">{challenge.description}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
                {challenge.variants.map((v) => (
                  <button
                    key={v.id}
                    data-testid={`variant-${v.id}`}
                    onClick={() => {
                      setVariantId(v.id);
                      setValues({});
                      setAssembled("");
                    }}
                    className={`${bigBtn} border-sand hover:border-orange hover:text-orange-dark`}
                  >
                    {v.label}
                    <span className="text-orange">&rarr;</span>
                  </button>
                ))}
              </div>
              <ChangeLink onClick={resetChallenge} />
            </div>
          ) : (
            spec && (
              <div data-testid="challenge-scaffold">
                <h3 className="font-display text-3xl leading-none mb-1">
                  {challenge.title}
                </h3>
                <p className="text-ink/60 mb-6">{challenge.description}</p>

                {/* Scope promise */}
                <div className="callout-guidance mb-8">
                  <p className="mono-label text-teal mb-1">{p3.scope_label}</p>
                  <p className="text-ink mb-2">{spec.scope.win}</p>
                  <p className="text-sm text-ink/60">
                    {p3.partial_label}: {spec.scope.partial}
                  </p>
                </div>

                {/* Intake scaffold */}
                <div className="space-y-4">
                  {spec.fields.map((f) => (
                    <div key={f.key}>
                      <label className="block mb-1">
                        <span className="font-semibold">{f.label}</span>
                        {f.helper && (
                          <span className="block text-sm text-ink/50">
                            {renderTokens(f.helper)}
                          </span>
                        )}
                      </label>
                      <textarea
                        data-testid={`intake-${f.key}`}
                        rows={2}
                        value={values[f.key] || ""}
                        onChange={(e) =>
                          setValues((v) => ({ ...v, [f.key]: e.target.value }))
                        }
                        className="w-full resize-none bg-white border border-sand focus:border-orange focus:outline-none px-3 py-2"
                      />
                    </div>
                  ))}
                </div>

                <div className="mt-6 flex items-center gap-4">
                  <button
                    data-testid="generate-prompt"
                    onClick={generate}
                    className="px-6 py-3 bg-orange hover:bg-orange-dark text-white transition-colors duration-150"
                  >
                    {p3.generate}
                  </button>
                </div>

                {assembled && (
                  <div className="mt-6 bg-white border border-sand p-5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="mono-label text-ink/50">
                        Your R-T-C-F prompt
                      </span>
                      <button
                        data-testid="copy-challenge-prompt"
                        onClick={copy}
                        className="text-sm border border-ink/20 px-4 py-1.5 hover:border-orange hover:text-orange-dark transition-colors duration-150"
                      >
                        {copied ? "Copied" : p3.copy}
                      </button>
                    </div>
                    <pre
                      data-testid="assembled-challenge-prompt"
                      className="whitespace-pre-wrap font-body text-ink/80"
                    >
                      {assembled}
                    </pre>
                  </div>
                )}

                {/* Stuck panel */}
                <div data-testid="stuck-panel" className="callout-warning mt-8">
                  <p className="mono-label text-orange-dark mb-1">
                    {p3.stuck_label}
                  </p>
                  <p className="text-ink/70">{spec.stuck}</p>
                </div>

                <ChangeLink onClick={resetChallenge} />
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

const ChallengeCards = ({ onPick }) => (
  <div>
    <p className="mono-label text-ink/60 mb-4">{p3.choose_label}</p>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {config.challenges.map((c, i) => (
        <button
          key={c.id}
          data-testid={`challenge-card-${c.id}`}
          onClick={() => onPick(c.id)}
          className="group bg-white border border-sand p-6 text-left hover:border-orange transition-colors duration-150 flex flex-col"
        >
          <span className="w-9 h-9 bg-teal text-white font-display text-xl leading-none flex items-center justify-center mb-4">
            {i + 1}
          </span>
          <span className="font-semibold mb-1">{c.title}</span>
          <span className="text-sm text-ink/60 mb-4">{c.description}</span>
          <span className="mt-auto text-orange group-hover:text-orange-dark transition-colors duration-150">
            &rarr;
          </span>
        </button>
      ))}
    </div>
  </div>
);

const ChangeLink = ({ onClick }) => (
  <button
    data-testid="change-challenge"
    onClick={onClick}
    className="mt-6 text-sm underline underline-offset-4 text-ink/60 hover:text-orange-dark transition-colors duration-150"
  >
    &larr; {p3.change}
  </button>
);
